import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { SelectButtonModule } from 'primeng/selectbutton';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { Api } from '@services/api.service';
import { AuthService } from '@services/auth.service';
import { ToastService } from '@services/toast.service';
import { IOrgCustomerSummary } from '@common-interfaces/organization-customer-views';
import { PageHeaderComponent } from '../../../components/shared/page-header/page-header.component';
import { StatCardComponent } from '../../../components/shared/stat-card/stat-card.component';
import { EmptyStateComponent } from '../../../components/shared/empty-state/empty-state.component';
import { debugLogger } from '@common-helpers/debug-logger';

type CustomerFilter = 'ALL' | 'MULTI_VENUE' | 'BLOCKED' | 'COMPANIES';

@Component({
    selector: 'organisation-customers-list',
    imports: [
        FormsModule,
        TranslatePipe,
        TableModule,
        TagModule,
        SelectButtonModule,
        InputTextModule,
        IconFieldModule,
        InputIconModule,
        PageHeaderComponent,
        StatCardComponent,
        EmptyStateComponent
    ],
    templateUrl: './organisation-customers-list.component.html'
})
export class OrganisationCustomerListComponent implements OnInit {
    private api = inject(Api);
    private auth = inject(AuthService);
    private router = inject(Router);
    private toast = inject(ToastService);

    loading = signal(true);
    search = signal('');
    filter = signal<CustomerFilter>('ALL');
    private customers = signal<IOrgCustomerSummary[]>([]);

    filterOptions: { value: CustomerFilter; label: string }[] = [
        { value: 'ALL', label: 'FILTER_ALL' },
        { value: 'MULTI_VENUE', label: 'FILTER_MULTI_VENUE' },
        { value: 'COMPANIES', label: 'FILTER_COMPANIES' },
        { value: 'BLOCKED', label: 'FILTER_BLOCKED' }
    ];

    stats = computed(() => {
        const all = this.customers();
        return {
            total: all.length,
            multiVenue: all.filter((c) => c.venueCount > 1).length,
            companies: all.filter((c) => c.isCompany).length,
            blocked: all.filter((c) => c.blackListed).length
        };
    });

    rows = computed(() => {
        const term = this.search().trim().toLowerCase();
        const filter = this.filter();
        return this.customers().filter((c) => {
            if (filter === 'MULTI_VENUE' && c.venueCount < 2) return false;
            if (filter === 'BLOCKED' && !c.blackListed) return false;
            if (filter === 'COMPANIES' && !c.isCompany) return false;
            if (!term) return true;
            return [c.firstName, c.lastName, `${c.firstName} ${c.lastName}`, c.email, c.phone, c.companyName].some((v) =>
                v?.toLowerCase().includes(term)
            );
        });
    });

    async ngOnInit(): Promise<void> {
        try {
            const res = await this.api.currentorganization().get<IOrgCustomerSummary[]>('/customers');
            if (res.success) {
                this.customers.set(res.data);
            }
        } catch (error) {
            debugLogger.error('Failed to load customers', error);
            this.toast.showError('Could not load customers.');
        } finally {
            this.loading.set(false);
        }
    }

    displayName(c: IOrgCustomerSummary): string {
        return `${c.firstName} ${c.lastName}`.trim() || c.email || c.phone || '—';
    }

    initials(c: IOrgCustomerSummary): string {
        return ((c.firstName?.[0] ?? '') + (c.lastName?.[0] ?? '')).toUpperCase() || (c.email?.[0] ?? '?').toUpperCase();
    }

    open(c: IOrgCustomerSummary): void {
        this.router.navigate(['/organizations', this.auth.organizationId(), 'crm', 'customers', c._id]);
    }
}
